/**
 * Bulk Purchase Module V2
 * Handles quantity selection, pricing updates, and cart functionality
 */

class BulkPurchaseV2 {
  constructor() {
    this.container = document.querySelector('.bulk-purchase-v2');
    if (!this.container) return;

    this.productData = this.loadProductData();
    this.selectedDays = 30;
    this.selectedQuantity = 1;
    this.purchaseType = 'onetime'; // Will be updated in init() based on metafield
    this.isUpdatingSellingPlans = false; // Track when linked variant switch is in progress
    this.currentProductId = this.productData?.productId; // Track current product for linked variant detection

    this.init();
    this.listenForVariantChanges();
  }

  loadProductData() {
    const dataElement = this.container.querySelector('script[data-product-data]');
    if (!dataElement) {
      console.error('Product data element not found');
      return null;
    }
    
    try {
      const data = JSON.parse(dataElement.textContent);
      return data;
    } catch (e) {
      console.error('Failed to parse product data:', e);
      return null;
    }
  }

  init() {
    this.bindQuantityButtons();
    this.bindPurchaseTypeButtons();
    this.bindAddToCart();
    
    // Set initial state without updating prices (already rendered by Liquid)
    const defaultSubscription = this.container.getAttribute('data-default-subscription') === 'true';
    this.selectedDays = 30;
    this.selectedQuantity = 1;
    this.purchaseType = defaultSubscription ? 'subscription' : 'onetime';
    
    // Update quantity labels if selling plans provide different names
    this.updateQuantityLabels();
    
    // Initialize proper visual states for cards
    this.initializeCardStates();
    
    // Update per-serving prices and hidden inputs to match initial purchase type
    this.updatePerServingPrices();
    this.updateHiddenInputs();
    
  }

  updateQuantityLabels() {
    if (!this.productData || !this.productData.quantityLabels) return;

    const labels = this.productData.quantityLabels;

    // Update each quantity card label if a custom label exists
    Object.keys(labels).forEach(days => {
      let label = labels[days];
      if (label) {
        const labelLower = label.toLowerCase();

        // Check for delivery frequency patterns and replace with bottle counts
        if (labelLower.includes('every month') || labelLower.includes('every 1 month')) {
          label = '1 Bottle';
        } else if (labelLower.includes('every 2 months')) {
          label = '2 Bottles';
        } else if (labelLower.includes('every 3 months')) {
          label = '3 Bottles';
        } else {
          // Fallback: Clean up the label by removing common prefixes
          label = label.replace(/^Delivery every\s+/i, '');
          label = label.replace(/^Delivered every\s+/i, '');
          label = label.replace(/^Ships every\s+/i, '');
          label = label.replace(/^Subscribe:\s+/i, '');

          // Capitalize first letter of each word
          label = label.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        }

        const card = this.container.querySelector(`[data-days="${days}"]`);
        if (card) {
          const labelElement = card.closest('.quantity-card')?.querySelector('.text-2xl');
          if (labelElement) {
            labelElement.textContent = label;
          }
        }
      }
    });
  }

  initializeCardStates() {
    // Set quantity card state based on this.selectedDays
    const quantityCards = this.container.querySelectorAll('.quantity-card');
    quantityCards.forEach(card => {
      const radio = card.querySelector('.quantity-radio');
      const cardInner = card.querySelector('.quantity-card-inner');

      if (parseInt(radio.dataset.days) === this.selectedDays) {
        radio.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
      } else {
        radio.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
      }
    });

    // Set initial purchase type state based on this.purchaseType
    const purchaseCards = this.container.querySelectorAll('.purchase-card');
    purchaseCards.forEach(card => {
      const input = card.querySelector('.purchase-type-input');
      const cardInner = card.querySelector('.purchase-card-inner');
      const radioButton = card.querySelector('.radio-button');
      const checkmarkIcon = card.querySelector('.checkmark-icon');

      if (input.dataset.type === this.purchaseType) {
        input.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.add('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.remove('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.remove('hidden');
      } else {
        input.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.remove('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.add('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.add('hidden');
      }
    });
  }

  bindQuantityButtons() {
    // Use event delegation on document to survive DOM replacement from section rendering
    document.addEventListener('change', (e) => {
      if (!e.target.matches('.quantity-radio')) return;
      const container = e.target.closest('.bulk-purchase-v2');
      if (!container || !e.target.checked) return;
      this.container = container;
      this.selectQuantityOption(parseInt(e.target.dataset.days));
    });
  }

  bindPurchaseTypeButtons() {
    // Use event delegation on document to survive DOM replacement from section rendering
    document.addEventListener('change', (e) => {
      if (!e.target.matches('.purchase-type-input')) return;
      const container = e.target.closest('.bulk-purchase-v2');
      if (!container || !e.target.checked) return;
      this.container = container;
      this.updatePurchaseType(e.target.dataset.type);
    });
  }

  bindAddToCart() {
    // Use event delegation on document to survive DOM replacement from section rendering
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.add-to-cart-v2');
      if (!btn) return;
      if (btn.disabled || this.isAddingToCart) return;
      const container = btn.closest('.bulk-purchase-v2');
      if (!container) return;
      this.container = container;
      e.preventDefault();
      this.addToCart();
    });
  }

  selectQuantityOption(days, skipPriceUpdate = false) {
    this.selectedDays = days;
    this.selectedQuantity = days === 30 ? 1 : days === 60 ? 2 : 3;

    /* BEGIN: PostHog Bulk Quantity Selected tracking (added 2026-02-24) */
    if (typeof posthog !== 'undefined') {
      posthog.capture('Bulk Quantity Selected', {
        days_supply: days,
        bottle_count: this.selectedQuantity,
        purchase_type: this.purchaseType,
        product_title: this.productData?.productTitle || document.title,
        source: 'bulk_form'
      });
    }
    /* END: PostHog Bulk Quantity Selected tracking */

    // Update radio buttons and card styling
    const quantityCards = this.container.querySelectorAll('.quantity-card');
    quantityCards.forEach(card => {
      const radio = card.querySelector('.quantity-radio');
      const cardInner = card.querySelector('.quantity-card-inner');
      
      if (parseInt(radio.dataset.days) === days) {
        radio.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
      } else {
        radio.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
      }
    });

    // Update subscription visibility based on whether a selling plan exists for this quantity
    this.updateSubscriptionVisibility();

    // Update shipping frequency text for subscriptions to match selling plan
    const shippingFreq = this.container.querySelector('.shipping-frequency');
    if (shippingFreq) {
      // Map days to proper frequency text regardless of selling plan name
      let freqText;
      if (days === 30) {
        freqText = 'Delivery every month';
      } else if (days === 60) {
        freqText = 'Delivery every 2 months';
      } else if (days === 90) {
        freqText = 'Delivery every 3 months';
      } else {
        freqText = 'Delivery every month';
      }
      
      shippingFreq.textContent = freqText;
    }

    if (!skipPriceUpdate) {
      this.updatePricing();
    }
  }

  updateSubscriptionVisibility() {
    // Check if subscription is available for current selection
    // First check for actual selling plan, then fallback to pricing data
    const hasSellingPlan = this.productData?.sellingPlans && this.productData.sellingPlans[this.selectedDays];
    const hasPricingData = this.productData?.actualPrices?.onetime && this.productData.actualPrices.onetime[this.selectedDays];
    const subscriptionAvailable = hasSellingPlan || hasPricingData;
    const subscriptionCard = this.container.querySelector('.subscription-card');
    const subscriptionInput = this.container.querySelector('#purchase-subscription');
    const subscriptionPriceElement = this.container.querySelector('.subscription-price');
    const subscriptionComparePriceElement = this.container.querySelector('.subscription-compare-price');
    const subscriptionFrequencyElement = this.container.querySelector('.subscription-frequency');
    
    if (!subscriptionAvailable) {
      // Disable subscription option if no selling plan
      if (subscriptionCard) {
        subscriptionCard.classList.add('opacity-50', 'pointer-events-none');
      }
      if (subscriptionInput) {
        subscriptionInput.disabled = true;
      }
      
      // Hide subscription pricing elements when not available
      if (subscriptionPriceElement) {
        subscriptionPriceElement.style.visibility = 'hidden';
      }
      if (subscriptionComparePriceElement) {
        subscriptionComparePriceElement.style.visibility = 'hidden';
      }
      if (subscriptionFrequencyElement) {
        subscriptionFrequencyElement.style.visibility = 'hidden';
      }
      
      // Switch to one-time if subscription was selected
      if (this.purchaseType === 'subscription') {
        this.updatePurchaseType('onetime');
      }
    } else {
      // Enable subscription option if selling plan exists
      if (subscriptionCard) {
        subscriptionCard.classList.remove('opacity-50', 'pointer-events-none');
      }
      if (subscriptionInput) {
        subscriptionInput.disabled = false;
      }
      
      // Show subscription pricing elements when available
      if (subscriptionPriceElement) {
        subscriptionPriceElement.style.visibility = 'visible';
      }
      if (subscriptionComparePriceElement) {
        subscriptionComparePriceElement.style.visibility = 'visible';
      }
      if (subscriptionFrequencyElement) {
        subscriptionFrequencyElement.style.visibility = 'visible';
      }
    }
  }

  updatePurchaseType(type) {
    this.purchaseType = type;

    /* BEGIN: PostHog Bulk Purchase Type Changed tracking (added 2026-02-24) */
    if (typeof posthog !== 'undefined') {
      posthog.capture('Purchase Type Changed', {
        purchase_type: type,
        days_supply: this.selectedDays,
        bottle_count: this.selectedQuantity,
        product_title: this.productData?.productTitle || document.title,
        source: 'bulk_form'
      });
    }
    /* END: PostHog Bulk Purchase Type Changed tracking */

    const purchaseCards = this.container.querySelectorAll('.purchase-card');
    
    purchaseCards.forEach(card => {
      const input = card.querySelector('.purchase-type-input');
      const cardInner = card.querySelector('.purchase-card-inner');
      const radioButton = card.querySelector('.radio-button');
      const checkmarkIcon = card.querySelector('.checkmark-icon');
      
      if (input.dataset.type === type) {
        input.checked = true;
        cardInner.classList.add('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.remove('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.add('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.remove('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.remove('hidden');
      } else {
        input.checked = false;
        cardInner.classList.remove('border-black', 'shadow-lg', 'bg-white');
        cardInner.classList.add('border-gray-300', 'bg-transparent');
        if (radioButton) {
          radioButton.classList.remove('bg-[#5F7C78]', 'border-[#5F7C78]');
          radioButton.classList.add('border-gray-300', 'bg-transparent');
        }
        if (checkmarkIcon) checkmarkIcon.classList.add('hidden');
      }
    });

    // Update quantity card visibility based on purchase type
    this.updateQuantityCardVisibility();
    
    // Update subscription/one-time pricing AND per-serving prices
    this.updateSubscriptionPricing();
    this.updatePerServingPrices(); // Add this to update per-serving prices when purchase type changes
    this.updateHiddenInputs();
  }

  updateQuantityCardVisibility() {
    const quantityCards = this.container.querySelectorAll('.quantity-card');
    
    if (this.purchaseType === 'subscription') {
      // Don't gray out quantity cards - all should be available for subscription
      quantityCards.forEach(card => {
        card.classList.remove('opacity-50', 'pointer-events-none');
      });
    } else {
      // Enable all quantity cards for one-time purchase
      quantityCards.forEach(card => {
        card.classList.remove('opacity-50', 'pointer-events-none');
      });
    }
  }

  updateHiddenInputs() {
    // Update hidden form inputs for cart submission
    const form = this.container.querySelector('.bulk-cart-form');
    if (!form) return;

    const bundleTypeInput = form.querySelector('.bundle-type');
    const bundleQuantityInput = form.querySelector('.bundle-quantity');
    const purchaseTypeInput = form.querySelector('.purchase-type');
    const timestampInput = form.querySelector('.timestamp');
    const supplyLabelInput = form.querySelector('.supply-label');
    const bundleLabelInput = form.querySelector('.bundle-label');
    const sellingPlanInput = form.querySelector('.selling-plan');
    const quantityInput = form.querySelector('.item-quantity');

    const bottleCount = this.selectedQuantity;
    if (bundleTypeInput) bundleTypeInput.value = `${bottleCount}_bottle${bottleCount > 1 ? 's' : ''}`;
    if (bundleQuantityInput) bundleQuantityInput.value = this.selectedQuantity.toString();
    if (purchaseTypeInput) purchaseTypeInput.value = this.purchaseType;
    if (timestampInput) timestampInput.value = Date.now().toString();
    if (quantityInput) quantityInput.value = this.selectedQuantity.toString();

    // Add bundle label for cart display
    if (this.selectedQuantity > 1) {
      if (bundleLabelInput) bundleLabelInput.value = `${this.selectedQuantity} Bottle${this.selectedQuantity > 1 ? 's' : ''}`;
      if (supplyLabelInput) supplyLabelInput.value = '';
    } else {
      if (supplyLabelInput) supplyLabelInput.value = `${this.selectedQuantity} Bottle${this.selectedQuantity > 1 ? 's' : ''}`;
      if (bundleLabelInput) bundleLabelInput.value = '';
    }

    // Add selling plan for subscriptions
    if (this.purchaseType === 'subscription') {
      const sellingPlan = this.productData?.sellingPlans?.[this.selectedDays];
      if (sellingPlan && sellingPlan.id && sellingPlanInput) {
        sellingPlanInput.value = sellingPlan.id;
      }
    } else {
      if (sellingPlanInput) sellingPlanInput.value = '';
    }
  }

  updatePricing() {
    this.updateSubscriptionPricing();
    this.updatePerServingPrices();
  }

  updateSubscriptionPricing() {
    if (!this.productData) return;

    const basePrice = this.productData.basePrice;
    const actualPrices = this.productData.actualPrices || {};
    
    // Calculate one-time purchase price - show discount preview (matches Shopify Function)
    let onetimePrice = basePrice * this.selectedQuantity;
    let onetimePriceWithoutDiscount = onetimePrice; // Full price for strikethrough
    
    // Apply same discounts as Shopify Function for preview:
    // 1 bottle: 0% discount
    // 2 bottles: 5% discount  
    // 3 bottles: 10% discount
    if (this.selectedDays === 60) {
      onetimePrice = onetimePrice * 0.95; // 5% discount preview
    } else if (this.selectedDays === 90) {
      onetimePrice = onetimePrice * 0.9; // 10% discount preview
    }
    
    // Don't use actualPrices for one-time pricing - let frontend show discount preview
    // The Shopify Function will handle actual discounts at checkout

    // Calculate subscription price using selling plans or default discount
    let subscriptionPrice;
    let subscriptionComparePrice = onetimePrice;
    let subscriptionFrequency = 'monthly';
    let subscriptionAvailable = false;
    
    if (this.productData.sellingPlans && this.productData.sellingPlans[this.selectedDays]) {
      // Use actual selling plan price from Shopify/Recharge
      const sellingPlan = this.productData.sellingPlans[this.selectedDays];
      // Multiply by quantity since selling plan price is per unit
      subscriptionPrice = sellingPlan.price * this.selectedQuantity;
      subscriptionAvailable = true;
      
      // Determine frequency based on days
      if (this.selectedDays === 60) {
        subscriptionFrequency = 'every 2 months';
      } else if (this.selectedDays === 90) {
        subscriptionFrequency = 'every 3 months';
      }
    } else if (this.productData?.actualPrices?.onetime && this.productData.actualPrices.onetime[this.selectedDays]) {
      // Fallback: Use pricing data with proper discount percentages when selling plans aren't synced
      // 1 bottle: 10% off, 2 bottles: 15% off, 3 bottles: 20% off
      let discountMultiplier = 0.9; // Default 10% off for 1 bottle
      if (this.selectedQuantity === 2) {
        discountMultiplier = 0.85; // 15% off for 2 bottles
      } else if (this.selectedQuantity === 3) {
        discountMultiplier = 0.8; // 20% off for 3 bottles
      }
      subscriptionPrice = onetimePrice * discountMultiplier;
      subscriptionAvailable = true;
      
      // Determine frequency based on days
      if (this.selectedDays === 60) {
        subscriptionFrequency = 'every 2 months';
      } else if (this.selectedDays === 90) {
        subscriptionFrequency = 'every 3 months';
      }
      
    } else {
      // No selling plan or pricing data available
      // 1 bottle: 10% off, 2 bottles: 15% off, 3 bottles: 20% off
      let discountMultiplier = 0.9; // Default 10% off for 1 bottle
      if (this.selectedQuantity === 2) {
        discountMultiplier = 0.85; // 15% off for 2 bottles
      } else if (this.selectedQuantity === 3) {
        discountMultiplier = 0.8; // 20% off for 3 bottles
      }
      subscriptionPrice = onetimePrice * discountMultiplier;
      subscriptionAvailable = false;
      
    }

    // Update subscription card availability
    const subscriptionCard = this.container.querySelector('.subscription-card');
    const subscriptionPriceElement = this.container.querySelector('.subscription-price');
    const subscriptionComparePriceElement = this.container.querySelector('.subscription-compare-price');
    const subscriptionFrequencyElement = this.container.querySelector('.subscription-frequency');
    
    if (subscriptionAvailable) {
      // Show subscription as available
      if (subscriptionCard) {
        subscriptionCard.classList.remove('opacity-50', 'pointer-events-none');
      }
      
      if (subscriptionPriceElement) {
        subscriptionPriceElement.textContent = this.formatMoney(subscriptionPrice);
      }
      
      if (subscriptionComparePriceElement) {
        if (subscriptionComparePrice > subscriptionPrice) {
          subscriptionComparePriceElement.textContent = this.formatMoney(subscriptionComparePrice);
          subscriptionComparePriceElement.classList.remove('hidden');
        } else {
          subscriptionComparePriceElement.classList.add('hidden');
        }
      }
      
      // Update discount percentage in subscription benefits
      this.updateSubscriptionDiscountText(subscriptionPrice, subscriptionComparePrice);
      
      if (subscriptionFrequencyElement) {
        // Update frequency text based on selected days
        if (this.selectedDays === 30) {
          subscriptionFrequencyElement.textContent = '/mo';
        } else if (this.selectedDays === 60) {
          subscriptionFrequencyElement.textContent = '/2mo';
        } else {
          subscriptionFrequencyElement.textContent = '/3mo';
        }
      }
    } else {
      // Disable subscription option when not available for this quantity
      if (subscriptionCard) {
        subscriptionCard.classList.add('opacity-50', 'pointer-events-none');
      }
      
      const subscriptionInput = this.container.querySelector('#purchase-subscription');
      if (subscriptionInput) {
        subscriptionInput.disabled = true;
      }
      
      // If subscription not available and it's selected, switch to one-time
      if (this.purchaseType === 'subscription') {
        this.updatePurchaseType('onetime');
      }
    }
    
    // Enable subscription card when available
    if (subscriptionAvailable && subscriptionCard) {
      subscriptionCard.classList.remove('opacity-50', 'pointer-events-none');
      const subscriptionInput = this.container.querySelector('#purchase-subscription');
      if (subscriptionInput) {
        subscriptionInput.disabled = false;
      }
    }

    // Delivery-cadence bullet: "N bottle(s) delivered every [month|N months]" — mirrors the
    // supply ladder (1/2/3 bottles => 30/60/90 days => every 1/2/3 months) so the subscribe
    // card states the cadence the same way the WHT treatment widget does.
    const cadenceEl = this.container.querySelector('[data-sub-cadence]');
    if (cadenceEl) {
      const cadenceNoun = this.selectedQuantity === 1 ? 'bottle' : 'bottles';
      let cadencePhrase = 'every month';
      if (this.selectedDays === 60) cadencePhrase = 'every 2 months';
      else if (this.selectedDays === 90) cadencePhrase = 'every 3 months';
      cadenceEl.textContent = `${this.selectedQuantity} ${cadenceNoun} delivered ${cadencePhrase}`;
    }

    // Update one-time pricing display
    const onetimePriceElement = this.container.querySelector('.onetime-price');
    const onetimeComparePriceElement = this.container.querySelector('.onetime-compare-price');
    
    if (onetimePriceElement) {
      onetimePriceElement.textContent = this.formatMoney(onetimePrice);
    }
    
    // Show compare price for one-time if there's a bulk discount
    if (onetimeComparePriceElement) {
      // Show strikethrough for 60 and 90 day options (which have discounts)
      if (this.selectedDays === 60 || this.selectedDays === 90) {
        onetimeComparePriceElement.textContent = this.formatMoney(onetimePriceWithoutDiscount);
        onetimeComparePriceElement.classList.remove('hidden');
      } else {
        onetimeComparePriceElement.classList.add('hidden');
      }
    }

    // Update inline price display (above subhead)
    const inlinePrice = document.querySelector('.pdp-inline-price');
    if (inlinePrice) {
      const displayPrice = this.purchaseType === 'subscription' ? subscriptionPrice : onetimePrice;
      inlinePrice.textContent = this.formatMoney(displayPrice);
    }
  }

  updateSubscriptionDiscountText(subscriptionPrice, comparePrice) {
    // Calculate discount percentage based on current selection
    let discountPercentage = 0;

    // If we have selling plan data, calculate actual discount from base price
    if (this.productData?.sellingPlans && this.productData.sellingPlans[this.selectedDays]) {
      const sellingPlan = this.productData.sellingPlans[this.selectedDays];
      const basePriceTotal = this.productData.basePrice * this.selectedQuantity;
      const sellingPlanTotal = sellingPlan.price * this.selectedQuantity;

      if (basePriceTotal > sellingPlanTotal) {
        discountPercentage = Math.round(((basePriceTotal - sellingPlanTotal) / basePriceTotal) * 100);
      }
    } else if (comparePrice > subscriptionPrice) {
      // Fallback calculation
      discountPercentage = Math.round(((comparePrice - subscriptionPrice) / comparePrice) * 100);
    }

    // Update first order discount text
    const discountTextElement = this.container.querySelector('.subscription-discount-text');
    if (discountTextElement && discountPercentage > 0) {
      discountTextElement.textContent = `Save ${discountPercentage}% on your first order`;
    } else if (discountTextElement) {
      discountTextElement.textContent = 'Save 10% on your first order';
    }

    // Update 2nd+ orders discount text (first order % + 5%)
    const repeatTextElement = this.container.querySelector('.subscription-repeat-text');
    if (repeatTextElement && discountPercentage > 0) {
      repeatTextElement.textContent = `Save ${discountPercentage + 5}% on your 2nd+ orders`;
    } else if (repeatTextElement) {
      repeatTextElement.textContent = 'Save 15% on your 2nd+ orders';
    }
  }

  updatePerServingPrices() {
    if (!this.productData) return;
    
    const servingElements = this.container.querySelectorAll('.price-per-serving');
    const basePrice = this.productData.basePrice;
    const servingsPerBottle = this.productData.servingsPerBottle || 30; // Default to 30 if not set
    
    servingElements.forEach(element => {
      const days = parseInt(element.dataset.days);
      const quantity = days === 30 ? 1 : days === 60 ? 2 : 3;
      
      let totalPrice;
      
      // Use different pricing based on purchase type
      if (this.purchaseType === 'subscription' && this.productData.sellingPlans && this.productData.sellingPlans[days]) {
        // Use subscription pricing from selling plans
        totalPrice = this.productData.sellingPlans[days].price * quantity;
      } else if (this.purchaseType === 'subscription') {
        // Subscription selected but no selling plan available - use fallback pricing
        totalPrice = basePrice * quantity;
        
        // Apply bulk discounts for subscription fallback pricing
        if (quantity === 1) {
          totalPrice = totalPrice * 0.9; // 10% discount for 1 bottle subscription
        } else if (quantity === 2) {
          totalPrice = totalPrice * 0.85; // 15% discount for 2 bottles subscription
        } else if (quantity === 3) {
          totalPrice = totalPrice * 0.8; // 20% discount for 3 bottles subscription
        }
      } else {
        // Use one-time pricing with bulk discounts (manual frontend discounts)
        totalPrice = basePrice * quantity;
        
        // Apply the same discounts as Shopify Function for display consistency
        if (quantity === 2) {
          totalPrice = totalPrice * 0.95; // 5% discount for 2 bottles
        } else if (quantity === 3) {
          totalPrice = totalPrice * 0.9; // 10% discount for 3 bottles
        }
        // 1 bottle gets 0% discount (no change)
      }
      
      // Calculate price per serving using servings per bottle
      const totalServings = servingsPerBottle * quantity;
      const pricePerServing = totalPrice / totalServings;
      
      const formattedPrice = `${this.formatMoney(pricePerServing)}/serving`;
      element.textContent = formattedPrice;
    });
  }

  async addToCart() {
    if (this.isAddingToCart) return;
    this.isAddingToCart = true;

    const button = this.container.querySelector('.add-to-cart-v2');
    const buttonText = button.querySelector('.button-text');
    const spinner = button.querySelector('.loading-spinner');

    // Show loading state
    button.disabled = true;
    buttonText.classList.add('hidden');
    spinner.classList.remove('hidden');

    try {
      const formData = this.buildCartData();

      if (!formData) {
        // Check if this is a subscription without valid selling plan (likely linked variant issue)
        if (this.purchaseType === 'subscription') {
          throw new Error('Subscription not available for this variant. Please select one-time purchase or try refreshing the page.');
        }
        throw new Error('Unable to build cart data');
      }
      
      const params = new URLSearchParams();
      for (const key in formData) {
        params.append(key, formData[key]);
      }

      let response;
      for (let attempt = 0; attempt < 5; attempt++) {
        if (attempt > 0) {
          await new Promise(r => setTimeout(r, 1500 * Math.pow(2, attempt - 1)));
        }
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString()
        });

        if (response.status === 429) continue;
        break;
      }

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();

      /* BEGIN: PostHog Bulk Add to Cart tracking (added 2026-02-24) */
      if (typeof posthog !== 'undefined') {
        posthog.capture('Add to Cart', {
          product_id: this.productData?.productId,
          product_title: this.productData?.productTitle || document.title,
          variant_id: this.productData?.variantId,
          price: this.productData?.basePrice ? (this.productData.basePrice / 100).toFixed(2) : undefined,
          quantity: this.selectedQuantity,
          days_supply: this.selectedDays,
          purchase_type: this.purchaseType,
          currency: window.Shopify?.currency?.active || 'USD',
          source: 'bulk_form'
        });
      }
      /* END: PostHog Bulk Add to Cart tracking */

      // Trigger cart drawer or update cart UI
      this.handleCartSuccess();
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleCartError(error);
    } finally {
      // Reset button state
      this.isAddingToCart = false;
      button.disabled = false;
      buttonText.classList.remove('hidden');
      spinner.classList.add('hidden');
    }
  }

  buildCartData() {
    if (!this.productData) {
      console.error('Product data not available');
      return null;
    }

    const data = {
      id: this.productData.variantId,
      quantity: this.selectedQuantity
    };

    // Generate unique timestamp at time of adding to cart
    const timestamp = Date.now().toString();

    // Add line item properties directly to the data object (not nested)
    data['properties[_bundle_quantity]'] = this.selectedQuantity.toString();
    data['properties[_hide_quantity]'] = 'true';
    data['properties[_purchase_type]'] = this.purchaseType;
    data['properties[_timestamp]'] = timestamp;
    
    // Set _bundle_type based on quantity for Shopify Function to read
    if (this.selectedQuantity === 1) {
      data['properties[_bundle_type]'] = '1_bottle';
    } else if (this.selectedQuantity === 2) {
      data['properties[_bundle_type]'] = '2_bottles';
    } else if (this.selectedQuantity === 3) {
      data['properties[_bundle_type]'] = '3_bottles';
    }
    
    // Add discount information for cart display
    if (this.purchaseType === 'onetime') {
      let discountPercent = 0;
      if (this.selectedQuantity === 2) discountPercent = 5;   // 2 bottles get 5% off
      if (this.selectedQuantity === 3) discountPercent = 10;  // 3 bottles get 10% off
      
      if (discountPercent > 0) {
        data['properties[_frontend_discount]'] = `${discountPercent}%`;
        data['properties[_original_price]'] = (this.productData.basePrice * this.selectedQuantity).toString();
      }
    } else if (this.purchaseType === 'subscription') {
      // Add strikethrough pricing for all subscription bundles
      const originalPrice = this.productData.basePrice * this.selectedQuantity;
      data['properties[_original_price]'] = originalPrice.toString();
      
      // Subscription discounts (from Recharge selling plans)
      let subscriptionDiscount = 0;
      if (this.selectedQuantity === 1) subscriptionDiscount = 10;  // 10% off for 1 bottle
      if (this.selectedQuantity === 2) subscriptionDiscount = 15;  // 15% off for 2 bottles
      if (this.selectedQuantity === 3) subscriptionDiscount = 20;  // 20% off for 3 bottles
      
      data['properties[_frontend_discount]'] = `${subscriptionDiscount}%`;
    }

    // Add bundle label for cart display
    let bundleLabel = '';
    if (this.selectedQuantity > 1) {
      bundleLabel = `${this.selectedQuantity} Bottle${this.selectedQuantity > 1 ? 's' : ''}`;
    } else {
      bundleLabel = `${this.selectedQuantity} Bottle${this.selectedQuantity > 1 ? 's' : ''}`;
    }
    
    // Add bundle property for both subscription and one-time
    data['properties[Bundle]'] = bundleLabel;
    
    // Add delivery frequency for subscription bundles
    if (this.purchaseType === 'subscription') {
      let deliveryFrequency = '';
      switch(this.selectedDays) {
        case 30:
          deliveryFrequency = 'Delivered monthly';
          break;
        case 60:
          deliveryFrequency = 'Delivered every 2 months';
          break;
        case 90:
          deliveryFrequency = 'Delivered every 3 months';
          break;
      }
      if (deliveryFrequency) {
        data['properties[Delivery Schedule]'] = deliveryFrequency;
        // Also add as hidden property for internal use
        data['properties[_delivery_frequency]'] = deliveryFrequency;
      }
    }

    // Add e-guide properties if product has an e-guide
    if (this.productData.eguide) {
      data['properties[_eguide_headline]'] = this.productData.eguide.headline || this.productData.eguide.title || '';
      if (this.productData.eguide.copy) {
        data['properties[_eguide_copy]'] = this.productData.eguide.copy;
      }
      const eguideUrl = typeof this.productData.eguide.url === 'object' ? (this.productData.eguide.url.value || this.productData.eguide.url.url || '') : this.productData.eguide.url;
      data['properties[_eguide_url]'] = eguideUrl;
      if (this.productData.eguide.image) {
        data['properties[_eguide_image]'] = this.productData.eguide.image;
      }
    }

    // Add selling plan for subscriptions
    if (this.purchaseType === 'subscription') {
      const sellingPlan = this.productData.sellingPlans[this.selectedDays];
      if (sellingPlan && sellingPlan.id) {
        data.selling_plan = sellingPlan.id;
        // For subscriptions, use the actual quantity (Recharge will handle this properly)
        data.quantity = this.selectedQuantity;
      } else {
        // No valid selling plan - this could be a race condition with linked variants
        console.error('No selling plan found for', this.selectedDays, 'days - cannot add subscription without selling plan');
        return null; // Return null to prevent adding invalid subscription
      }
    } else {
      // For one-time purchases, always use the selected quantity
      data.quantity = this.selectedQuantity;
    }

    return data;
  }

  handleCartSuccess() {
    // Update cart icon bubble to reflect new count
    this.updateCartIconBubble();
    
    // Trigger cart drawer if available
    if (typeof fetchCartDrawer === 'function') {
      fetchCartDrawer();
    } else {
      // Fallback: reload cart or show success message
      window.location.href = '/cart';
    }
  }

  async updateCartIconBubble() {
    try {
      // Fetch the cart-icon-bubble section
      const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newCartIcon = html.querySelector('.shopify-section');
      
      if (newCartIcon) {
        const currentCartIcon = document.getElementById('cart-icon-bubble');
        if (currentCartIcon) {
          currentCartIcon.innerHTML = newCartIcon.innerHTML;
        }
      }
    } catch (error) {
      console.error('Failed to update cart icon:', error);
    }
  }

  handleCartError(error) {
    // Show error message to user
    const button = this.container.querySelector('.add-to-cart-v2');
    const originalText = button.querySelector('.button-text').textContent;
    
    button.querySelector('.button-text').textContent = 'Error - Please try again';
    button.classList.add('bg-red-600', 'hover:bg-red-700');
    button.classList.remove('bg-emerald-600', 'hover:bg-emerald-700');
    
    setTimeout(() => {
      button.querySelector('.button-text').textContent = originalText;
      button.classList.remove('bg-red-600', 'hover:bg-red-700');
      button.classList.add('bg-emerald-600', 'hover:bg-emerald-700');
    }, 3000);
  }

  formatMoney(cents) {
    // Format cents to currency
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  }

  listenForVariantChanges() {
    // Listen for variant changes from the theme's variant selector
    document.addEventListener('variant:change', (event) => {
      this.handleVariantChange(event.detail);
    });

    // Also listen for manual variant selector changes (common Shopify themes)
    const variantSelectors = document.querySelectorAll('[name="id"], .variant-selector, .product-variant-id, select[data-variant-selector], input[type="radio"][name*="variant"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (event) => {
        // Try to get variant ID from the selector
        const variantId = event.target.value;
        if (variantId) {
          // Fetch variant data from Shopify API
          this.fetchVariantData(variantId);
        }
      });
    });

    // Listen for custom variant update events (some themes use this)
    window.addEventListener('variantChange', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });

    // Also support the standard Shopify variant change via history state
    const self = this;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function() {
      originalPushState.apply(history, arguments);
      setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          self.fetchVariantData(variantId);
        }
      }, 0);
    };
    
    history.replaceState = function() {
      originalReplaceState.apply(history, arguments);
      setTimeout(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          self.fetchVariantData(variantId);
        }
      }, 0);
    };
  }

  async fetchVariantData(variantId) {
    try {
      // Get product handle from the page or meta tag
      let productHandle = window.location.pathname.split('/').pop();
      
      // Remove any query parameters from handle
      if (productHandle.includes('?')) {
        productHandle = productHandle.split('?')[0];
      }
      
      // Fetch full product data from Shopify API
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      
      // Find the selected variant
      const variant = productData.variants.find(v => v.id == variantId);
      
      if (variant) {
        // Try to get selling plan allocations from the product's selling_plan_groups
        if (productData.selling_plan_groups && productData.selling_plan_groups.length > 0) {
          // Create selling plan allocations array for the variant
          variant.selling_plan_allocations = [];
          
          productData.selling_plan_groups.forEach(group => {
            if (group.selling_plans) {
              group.selling_plans.forEach(plan => {
                // Check if this plan applies to this variant
                // Most Shopify themes apply all plans to all variants by default
                const allocation = {
                  selling_plan_id: plan.id,
                  selling_plan: plan,
                  // Calculate price with plan discount if available
                  price: variant.price
                };
                
                // Apply percentage discount if present in plan
                if (plan.price_adjustments && plan.price_adjustments.length > 0) {
                  const adjustment = plan.price_adjustments[0];
                  if (adjustment.value_type === 'percentage' && adjustment.value) {
                    allocation.price = Math.round(variant.price * (1 - adjustment.value / 100));
                  } else if (adjustment.value_type === 'fixed_amount' && adjustment.value) {
                    allocation.price = variant.price - adjustment.value;
                  }
                }
                
                variant.selling_plan_allocations.push(allocation);
              });
            }
          });
        }
        
        this.handleVariantChange(variant);
      }
    } catch (error) {
      console.error('Error fetching variant data:', error);
    }
  }

  handleVariantChange(variant) {
    if (!variant) return;

    // Refresh container reference in case DOM was replaced by section rendering
    const freshContainer = document.querySelector('.bulk-purchase-v2');
    if (freshContainer && freshContainer !== this.container) {
      this.container = freshContainer;
      // Re-apply current selection states to the new DOM elements
      this.initializeCardStates();
    }

    // Detect if this is a linked variant (different product) vs normal variant (same product)
    // Linked variants will have productId in the event detail from custom-variants.js
    const isLinkedVariant = variant.productId && variant.productId !== this.currentProductId;

    if (isLinkedVariant) {
      this.currentProductId = variant.productId;
    }

    // Update product data with new variant information
    this.productData.variantId = variant.id;
    this.productData.basePrice = variant.price;
    this.productData.comparePrice = variant.compare_at_price || variant.price;

    // Update servings per bottle
    // Priority: 1) Linked variant data, 2) Variant servings map, 3) Product default, 4) 30
    if (isLinkedVariant && variant.servingsPerBottle) {
      // For linked variants, use the servings from the linked product's data
      this.productData.servingsPerBottle = variant.servingsPerBottle;
    } else {
      // For standard variants, check the variant servings map
      const variantServings = this.productData.variantServings || {};
      const variantIdStr = String(variant.id);
      const variantIdNum = Number(variant.id);
      const variantSpecificServings = variantServings[variantIdStr] || variantServings[variantIdNum];

      if (variantSpecificServings) {
        this.productData.servingsPerBottle = variantSpecificServings;
      } else if (variant.servingsPerBottle) {
        // Fallback to servings from variant event if available
        this.productData.servingsPerBottle = variant.servingsPerBottle;
      } else {
        // Fall back to product-level servings (or default 30)
        this.productData.servingsPerBottle = this.productData.productServingsPerBottle || 30;
      }
    }

    // Update selling plans if variant has them
    if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
      this.updateSellingPlansFromVariant(variant);
    } else {
    }

    // Recalculate and update all pricing displays
    this.updatePricing();
    this.updateHiddenInputs();
    this.updateSubscriptionVisibility();

    // Update per-serving prices in quantity cards
    this.updatePerServingPricesInCards();
  }

  updateSellingPlansFromVariant(variant) {
    // Keep existing selling plans as backup
    const existingPlans = this.productData.sellingPlans || {};
    
    // Reset selling plans
    this.productData.sellingPlans = {};
    
    if (!variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
      // Use fallback pricing with standard discounts
      this.productData.sellingPlans = {
        '30': {
          price: Math.round(this.productData.basePrice * 0.9),  // 10% off for 1 bottle
          comparePrice: this.productData.basePrice,
          name: '1 Bottle'
        },
        '60': {
          price: Math.round(this.productData.basePrice * 0.85), // 15% off for 2 bottles
          comparePrice: this.productData.basePrice,
          name: '2 Bottles'
        },
        '90': {
          price: Math.round(this.productData.basePrice * 0.8),  // 20% off for 3 bottles
          comparePrice: this.productData.basePrice,
          name: '3 Bottles'
        }
      };
      return;
    }
    
    // Map selling plans by position: first = 30, second = 60, third = 90
    const daysMapping = ['30', '60', '90'];
    
    variant.selling_plan_allocations.forEach((allocation, index) => {
      if (index >= 3) return; // Only use first 3 plans
      
      const plan = allocation.selling_plan;
      if (!plan) return;
      
      const days = daysMapping[index];
      
      this.productData.sellingPlans[days] = {
        id: allocation.selling_plan_id || plan.id,
        price: allocation.price || this.productData.basePrice,
        comparePrice: allocation.compare_at_price || this.productData.comparePrice,
        name: plan.name
      };
    });
    
    // Update quantity labels with selling plan names
    if (this.productData.sellingPlans) {
      this.productData.quantityLabels = {};
      Object.keys(this.productData.sellingPlans).forEach(days => {
        const plan = this.productData.sellingPlans[days];
        if (plan && plan.name) {
          // Store the original plan name - updateQuantityLabels() will handle the conversion
          this.productData.quantityLabels[days] = plan.name;
        }
      });
      // Update the labels in the UI (this will apply the "every month" -> "1 Bottle" logic)
      this.updateQuantityLabels();
    }
    
  }

  updatePerServingPricesInCards() {
    // Update the per-serving price display in each quantity card
    const cards = this.container.querySelectorAll('.quantity-card');
    const servingsPerBottle = this.productData.servingsPerBottle || 30; // Default to 30 if not set
    
    cards.forEach(card => {
      const radio = card.querySelector('.quantity-radio');
      const priceElement = card.querySelector('.price-per-serving');
      
      if (!radio || !priceElement) return;
      
      const days = parseInt(radio.dataset.days);
      const quantity = days === 30 ? 1 : days === 60 ? 2 : 3;
      
      let totalPrice = this.productData.basePrice * quantity;
      
      // Apply discounts based on current purchase type
      if (this.purchaseType === 'subscription' && this.productData.sellingPlans && this.productData.sellingPlans[days]) {
        totalPrice = this.productData.sellingPlans[days].price * quantity;
      } else if (this.purchaseType === 'onetime') {
        // Apply bulk discounts for one-time purchases
        if (days === 60) {
          totalPrice = totalPrice * 0.95; // 5% discount
        } else if (days === 90) {
          totalPrice = totalPrice * 0.9; // 10% discount
        }
      }
      
      const totalServings = servingsPerBottle * quantity;
      const pricePerServing = totalPrice / totalServings;
      priceElement.textContent = `${this.formatMoney(pricePerServing)}/serving`;
    });
  }
}

// Initialize once when DOM is ready (prevent duplicate instances from defer + DOMContentLoaded race)
if (!window._bulkPurchaseV2Initialized) {
  window._bulkPurchaseV2Initialized = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new BulkPurchaseV2();
    });
  } else {
    new BulkPurchaseV2();
  }
}